/*
 * Cart
 * ------------------------------------------------------------------
 * Interface for all cart functionality
 * - Built on @shopify/theme-cart
 * - Extends to add addItems(items)
 * - Private methods to update, show, rebuild cart
 */
import {waitUntil, WAIT_FOREVER} from 'async-wait-until';
import * as cartApi from '../components/api/theme-cart';
import * as Klaviyo from '../components/klaviyo-tracking';
import $ from 'cash-dom';
import {createFocusTrap} from 'focus-trap';

const selectors = {
  cartButton: '[data-cart-button]',
  cartButtonText: '[data-cart-button-text]',
  cartCountMessage: '[data-cart-count-message]',
  cartDrawer: '[data-cart-drawer]',
  cartClose: '[data-close-cart]',
  cartItems: '[data-cart-items]',
  cartItem: '[data-cart-item]',
  cartRemove: '[data-cart-remove]',
  cartRemoveItem: '[data-item-quantity-remove]',
  cartAddItem: '[data-item-quantity-add]',
  cartQuantityItem: '[data-item-quantity-input]',
  cartSubtotal: '[data-cart-subtotal] span',
  cartSubtotalOriginal: '[data-cart-subtotal-original] span',
  cartAfterpay: '[data-cart-afterpay]',
  cartFreeGift: '[data-cart-free-gift]',
  cartUpsell: '[data-cart-upsell]',
  cartUpsellItem: '[data-cart-upsell-item]',
  cartSample: '[data-cart-samples]',
  cartSampleItem: '[data-sample-item]',
  cartGiftWrap: '[data-cart-gift-wrap]',
  cartGiftMessage: '[data-cart-gift-message]',
  cartCharactersLeft: '[data-characters-left]',
  cartDrawerFooter: '[data-cart-drawer-footer]',
  cartDrawerBottom: '[data-cart-drawer-bottom]',
  cartDrawerThreshold: '[data-cart-drawer-threshold]',
  cartDrawerEmpty: '[data-cart-drawer-empty]',
  cartScrollArea: '.cart-drawer__scroll-area',
  sampleItem: '[data-sample-item]',
  cartShippingAlert: '[data-cart-shipping-alert]',
  cartSubheadingDefault: '[data-cart-subheading-default]',
  cartSubheadingMinimum: '[data-cart-subheading-minimum]',
  cartDiscount: '[data-cart-discount]',
  cartDiscountLink: '[data-cart-discount-link]',
};

const backdrop = document.querySelector('.backdrop');
const cartDrawer = document.querySelector(selectors.cartDrawer);
const cartDrawerFooter = document.querySelector(selectors.cartDrawerFooter);
const cartDrawerBottom = document.querySelector(selectors.cartDrawerBottom);
const cartDrawerThreshold = document.querySelector(
  selectors.cartDrawerThreshold
);
const cartDrawerEmpty = document.querySelector(selectors.cartDrawerEmpty);
const cartButton = document.querySelector(selectors.cartButton);
const cartButtonText = document.querySelector(selectors.cartButtonText);
const cartCountMessage = document.querySelector(selectors.cartCountMessage);
const cartUpsell = cartDrawer.querySelector(selectors.cartUpsell);
const cartSample = cartDrawer.querySelector(selectors.cartSample);
const cartGiftWrap = cartDrawer.querySelector(selectors.cartGiftWrap);

const cartSubheadingDefault = cartDrawer.querySelector(
  selectors.cartSubheadingDefault
);
const cartSubheadingDefaultText = cartSubheadingDefault.innerText;

const cartTrapFocus = createFocusTrap(cartDrawer, {
  initialFocus: false,
  allowOutsideClick: true,
});

// Set all cart drawer focusable elements to tabindex -1
const cartDrawerFocusable = cartDrawer.querySelectorAll(
  'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
);
cartDrawerFocusable.forEach(function (element) {
  element.setAttribute('tabindex', '-1');
});
cartDrawer.setAttribute('tabindex', '-1');
cartDrawer.setAttribute('aria-hidden', 'true');

const upsellItems = [];
let upsellItemProductIds = [];
const cartUpsellItem = cartUpsell.querySelectorAll(selectors.cartUpsellItem);
cartUpsellItem.forEach(function (upsellItem) {
  const addToCartButton = upsellItem.querySelector('[data-variant-id]');
  const upsellItemId = Number(addToCartButton.dataset.variantId);
  const upsellProductId = Number(upsellItem.dataset.cartUpsellItem);
  upsellItems.push(upsellItemId);
  upsellItemProductIds.push(upsellProductId);
});

if (cartSample) {
  const sampleItems = [];
  const cartSampleItem = cartSample.querySelectorAll(selectors.cartSampleItem);
  cartSampleItem.forEach(function (sampleItem) {
    const addToCartButton = sampleItem.querySelector('[data-variant-id]');
    const upsellItemId = Number(addToCartButton.dataset.variantId);
    sampleItems.push(upsellItemId);
  });
}

const cartFreeGift = cartDrawer.querySelector(selectors.cartFreeGift);
const freeGiftItems = [];

const cartItems = cartDrawer.querySelector(selectors.cartItems);

let bundleProducts = [];
let hideBundle = false;

let timings = [];
export function startCartTimer() {
  return Date.now();
}

export function endTimer(startTime) {
  const endTime = Date.now();

  const delay = endTime - startTime;
  timings.push(delay);
  const averageDelay = timings.reduce((a, b) => a + b, 0) / timings.length;

  console.log(`Cart API delay: ${delay}ms`);
  console.log(`Cart API average delay: ${averageDelay}ms`);
}

export function addItem(id, options, location, atc_type) {
  const startTime = startCartTimer();

  cartApi.addItem(id, options).then((item) => {
    endTimer(startTime);
    
    // Premature update cart button count
    // Potential issues will be resolved with cart status fetch
    let quantity;
    if (options === undefined) {
      quantity = 1;
    } else {
      quantity = options.quantity;
    }
    // Increase Cart Count
    increaseCartCount(quantity);

    // Open Cart
    openCartDrawer();

    // Render new item
    if (item) {
      renderItem(item, {
        prepend: true,
      });
    }

    const itemKey = item.key;
    const thisCartItem = cartDrawer.querySelector(
      `[data-cart-item="${itemKey}"]`
    );

    if (thisCartItem) {
      delete thisCartItem.dataset.itemPending;
    }

    updateCart();
    refreshCart();

    // Tracking
    getShadeRecommendations();
    Klaviyo.trackAddToCart(item);

    if (location === 'cart') {
      gtmTrackCartAddToCart(item, atc_type);
    }
  })
  .catch((error) => {
    if (options.soldoutRedirect) {
      window.location.href = options.soldoutRedirect.href;
    }
  });

}

export function removeItems(items) {
  let formData = {
    updates: items,
  };

  // Push items keys to array
  const itemKeys = [];
  Object.entries(items).forEach(([key]) => {
    itemKeys.push(key);
  });

  fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  })
    .then((response) => response.json())
    .then((data) => {
      gtmTrackRemoveItem(itemKeys);
      const cartItem = cartDrawer.querySelectorAll(selectors.cartItem);
      cartItem.forEach(function (item) {
        item.remove();
      });
      bundleProducts = [];
      data.items.forEach(function (item) {
        renderItem(item);
      });
      updateCart(data);
      setCartCount(data);

      updateSampleCards(data);
      getShadeRecommendations();
      //updateUpsellCards(data);
    })
    .catch((error) => {
      console.error('Error:', error);
    });
}

export function addItems(items) {
  let formData = {
    items: items,
  };

  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  })
    .then((response) => response.json())
    .then((data) => {

      gtmTrackBundleAddToCart(data.items);

      // Open Cart
      openCartDrawer();

      bundleProducts = [];
      data.items.forEach(function (item) {
        // Render new item
        if (item) {
          renderItem(item);
        }

        const itemKey = item.key;
        const thisCartItem = cartDrawer.querySelector(
          `[data-cart-item="${itemKey}"]`
        );
        delete thisCartItem.dataset.itemPending;

        // Tracking
        getShadeRecommendations();
      });

      updateCart();
      refreshCart();

      const cartItem = cartDrawer.querySelectorAll(selectors.cartItem);
      cartItem.forEach(function (item) {
        item.remove();
      });

      bundleProducts = [];
    })
    .catch((error) => {
      console.error('Error:', error);
    });
}


export function addSellingPlan(item) {
  let formData = {
    items: [item],
  };

  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  })
    .then((response) => response.json())
    .then((data) => {
      // Open Cart
      openCartDrawer();

      data.items.forEach(function (item) {
        // Render new item
        if (item) {
          renderItem(item);
        }

        const itemKey = item.key;
        const thisCartItem = cartDrawer.querySelector(
          `[data-cart-item="${itemKey}"]`
        );
        delete thisCartItem.dataset.itemPending;
      });

      updateCart();
      refreshCart();
    });
}

export function initCart() {
  const cartItem = cartDrawer.querySelectorAll(selectors.cartItem);

  let freeSampleItem = false;
  let freeGiftItem = false;
  let giftWrapItem = false;

  cartItem.forEach(function (item) {
    addItemEvents(item);
    // Show free sample option if there is a match
    const variantId = Number(item.dataset.cartVariantId);
    if (upsellItems.includes(variantId)) {
      freeSampleItem = true;
    }

    if (
      item
        .querySelector('[data-cart-line-price] span')
        .textContent.includes('Cyber Monday')
    ) {
      freeGiftItem = true;
    }

    if (
      item
        .querySelector('[data-item-product-title]')
        .textContent.toLowerCase()
        .includes('gift wrapping')
    ) {
      giftWrapItem = true;
    }
  });

  if (!giftWrapItem && cartItem.length > 0) {
    cartGiftWrap.classList.remove('hidden');
  }

  if (!freeSampleItem && cartItem.length > 0 && cartUpsell) {
    cartUpsell.classList.remove('hidden');
  }

  if (cartFreeGift && !freeGiftItem && cartItem.length > 0) {
    cartFreeGift.classList.remove('hidden');
  }

  // Setup free sample
  const cartUpsellItem = cartUpsell.querySelectorAll(selectors.cartUpsellItem);
  cartUpsellItem.forEach(function (item) {
    const addToCartButton = item.querySelector('[data-variant-id]');
    const variantId = Number(addToCartButton.dataset.variantId);
    const productId = Number(item.dataset.cartUpsellItem);
    
    addToCartButton.addEventListener('click', function (event) {
      if (this.dataset.link) {
        window.location = this.dataset.link;
      } else {
        addItem(variantId, {}, 'cart', 'upsell');
      }

      event.preventDefault();
    });

    const links = item.querySelectorAll('a');
    const productLink = links[0].href;
    links.forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        window.location = productLink;
      });
    });
  });

  // Setup empty product
  const cartEmptyItem = cartDrawer.querySelectorAll('.cart-drawer__empty-item');
  cartEmptyItem.forEach(function (item) {
    const addToCartButton = item.querySelector('[data-variant-id]');
    if (!addToCartButton) return;

    const variantId = Number(addToCartButton.dataset.variantId);
    addToCartButton.addEventListener('click', function (event) {
      addItem(variantId, {}, 'cart', 'empty');

      event.preventDefault();
    });
  });

  // Setup samples
  const cartSampleItem = cartSample.querySelectorAll(selectors.cartSampleItem);
  const cartScrollArea = cartDrawer.querySelector(selectors.cartScrollArea);
  cartSampleItem.forEach(function (item) {
    const addToCartButton = item.querySelector('[data-variant-id]');
    const variantId = Number(addToCartButton.dataset.variantId);
    addToCartButton.addEventListener('click', function (event) {
      addItem(variantId, {}, 'cart', 'samples');
      cartScrollArea.scrollTop = 0;

      event.preventDefault();
    });
  });

  // Setup gift wrapping
  const cartGiftWrapButton = cartGiftWrap.querySelector('[data-variant-id]');
  const giftWrapVariantId = Number(cartGiftWrapButton.dataset.variantId);
  cartGiftWrapButton.addEventListener('click', function (event) {
    addItem(giftWrapVariantId, {}, 'cart', 'gift wrapping');

    cartGiftWrap.classList.add('hidden');
    event.preventDefault();
  });

  cartApi.getState().then((state) => {
    updateSampleCards(state);
    updateUpsellCards(state);
  });

  // Shade recommendations
  getShadeRecommendations();

  autoAddProduct(theme.cartData);
  importProductRecommendations();
  removeInvalidUtmCampaignPromos();
  updateProgressBars(theme.cartData);

  // TODO: remove before launch
  // openCartDrawer();
  // If #cart is in the URL, open the cart drawer
  if (window.location.hash === '#cart') {
    openCartDrawer();
  }

  initYotpoLoyalty();
}

function removeInvalidUtmCampaignPromos() {
  // get the utmCampaign from the localStorage
  const utmParams = JSON.parse(sessionStorage.getItem('utmParams'));
  let utmCampaign = false;
  if (utmParams) {
    utmCampaign = utmParams.utm_campaign;
  }

  const campaignProgressBar = cartDrawer.querySelectorAll(
    `.cart-drawer__progress-bar[data-utm-campaign]:not([data-utm-campaign=""])`
  );
  const campaignProgressMessage = cartDrawer.querySelectorAll(
    `.cart-drawer__progress-message[data-utm-campaign]:not([data-utm-campaign=""])`
  );

  campaignProgressBar.forEach((bar) => {
    if (bar.dataset.utmCampaign !== '' && bar.dataset.utmCampaign !== utmCampaign) {
      const thresholdStart = Number(bar.dataset.thresholdStart);
      // Apply thresholdStart to the next bar
      const nextBar = bar.nextElementSibling;
      if (nextBar) {
        nextBar.dataset.thresholdStart = thresholdStart;
      }
      bar.remove();
    }
  });

  campaignProgressMessage.forEach((message) => {
    if (message.dataset.utmCampaign !== '' && message.dataset.utmCampaign !== utmCampaign) {
      message.remove();
    }
  });
}

function applyShadeRecommendations() {
  window.recommendedShades = [];
  let recommendation = {};

  const currentUrl = new URL(window.location.href);
  const currentUrlLastSegment = currentUrl.pathname.split('/').pop();

  const recommendedQuery = currentUrl.searchParams.get('recommended');
  const recommendedSourceQuery = currentUrl.searchParams.get(
    'recommendedSource'
  )
    ? currentUrl.searchParams.get('recommendedSource')
    : '';
  if (recommendedQuery) {
    const recommendedQueryArray = recommendedQuery
      ? recommendedQuery.split(',')
      : [];

    recommendation = {
      source: 'query',
      sourceProduct: recommendedSourceQuery,
      sourceProductTitle: '',
      recommendedProduct: currentUrlLastSegment,
      recommendedVariants: recommendedQueryArray,
    };

    window.recommendedShades.push(recommendation);
  }

  // Get recommended shades from sessionStorage
  // TODO: add back once recommendation data is final
  /*
  const sessionRecommendedShades = JSON.parse(
    sessionStorage.getItem('ShadeRecommendations')
  );
  if (sessionRecommendedShades) {
    window.recommendedShades = [
      ...window.recommendedShades,
      ...sessionRecommendedShades,
    ];
  }
  */

  const localRecommendedShades = JSON.parse(
    localStorage.getItem('ShadeRecommendations')
  );
  if (localRecommendedShades) {
    window.recommendedShades = [
      ...window.recommendedShades,
      ...localRecommendedShades,
    ];
  }

  // Look for matches in window.recommendedShades to currentUrlLastSegment
  const recommendedShades = window.recommendedShades.filter(
    (shade) => shade.recommendedProduct === currentUrlLastSegment
  );

  if (recommendedShades.length > 0) {
    const currentForm = document.querySelector(`form[data-product-handle="${currentUrlLastSegment}"]`);
    if (!currentForm) return;

    const shadeRecommendationsLabel = currentForm.querySelector(`[data-shade-recommendations]`);
    const shadeRecommendationsLabelSource =
      shadeRecommendationsLabel.querySelector('[data-shade-recommendations-source]');
    shadeRecommendationsLabel.classList.remove('hidden');

    const quizRecommendations = recommendedShades.filter(
      (shade) => shade.source === 'quiz'
    );
    const addToCartRecommendations = recommendedShades.filter(
      (shade) => shade.source === 'addToCart'
    );
    const queryRecommendation = recommendedShades.filter(
      (shade) => shade.source === 'query'
    );

    let activeShades;

    if (quizRecommendations.length > 0) {
      shadeRecommendationsLabelSource.innerHTML = `- Based on Shade Match Quiz. <a href="/pages/complexion-shade-finder-quiz" class="underline font-bold hover:text-accent">Retake Quiz</a>`;
      activeShades = quizRecommendations[0].recommendedVariants;

    } else if (addToCartRecommendations.length > 0) {

      // Break down addToCartRecommendations into the ranked sourceProducts
      const sourceFoundationProducts = addToCartRecommendations.filter(
        (shade) => shade.sourceProduct === 'revealer-skin-improving-foundation-spf-25'
      );
      const sourceTFOProducts = addToCartRecommendations.filter(
        (shade) => shade.sourceProduct === 'tinted-face-oil-foundation'
      );
      const sourceRevealerProducts = addToCartRecommendations.filter(
        (shade) => shade.sourceProduct === 'revealer-concealer'
      );
      const sourceCloudsetProducts = addToCartRecommendations.filter(
        (shade) => shade.sourceProduct === 'cloud-set'
      );

      let priorityAddToCartRecommendations = [];
      if (sourceFoundationProducts.length > 0) {
        priorityAddToCartRecommendations = sourceFoundationProducts;
      } else if (sourceTFOProducts.length > 0) {
        priorityAddToCartRecommendations = sourceTFOProducts;
      } else if (sourceRevealerProducts.length > 0) {
        priorityAddToCartRecommendations = sourceRevealerProducts;
      } else if (sourceCloudsetProducts.length > 0) {
        priorityAddToCartRecommendations = sourceCloudsetProducts;
      }

      if (priorityAddToCartRecommendations.length === 1) {
        shadeRecommendationsLabelSource.innerHTML = `- Based on <strong>${priorityAddToCartRecommendations[0].sourceProductTitle}</strong> in the bag.`;
        activeShades = priorityAddToCartRecommendations[0].recommendedVariants;
      } else {
        shadeRecommendationsLabelSource.innerHTML = ` 
          <details class="inline-block relative" data-recommendation-selector>
            <summary class="cursor-pointer">- Based on <span class="font-bold underline">${
              priorityAddToCartRecommendations[0].sourceProductTitle
            }</span> in the bag.</summary>
            <ul class="absolute left-0 bg-gray-200 shadow-md z-50">
              ${priorityAddToCartRecommendations
                .map((recommendation) => {
                  return `<li>
                  <a href="/products/${recommendation.recommendedProduct}"
                    class="group block whitespace-nowrap underline p-3 font-bold hover:bg-white hover:text-accent"
                    data-recommended-variants="${recommendation.recommendedVariants}">
                    <svg aria-hidden="true" width="9" height="9" class="opacity-0 group-hover:opacity-100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.757 1.498a.203.203 0 0 0 0 .38l.993.372.373.993a.203.203 0 0 0 .38 0l.372-.993.993-.373a.203.203 0 0 0 0-.38l-.993-.372-.373-.993A.203.203 0 0 0 7.312 0a.203.203 0 0 0-.19.132l-.372.993-.993.373Zm-2.152-.21a.28.28 0 0 0-.51 0l-.928 2.004-2.004.927a.28.28 0 0 0 0 .511l2.006.927.925 2.004a.28.28 0 0 0 .51 0l.926-2.006 2.006-.926a.28.28 0 0 0 0-.51l-2.004-.925-.927-2.006ZM6.75 6.75l-.993.373a.203.203 0 0 0-.132.19c0 .084.053.16.132.19l.993.372.373.993a.203.203 0 0 0 .38 0l.372-.993.993-.373A.203.203 0 0 0 9 7.312a.203.203 0 0 0-.132-.19l-.993-.372-.373-.993a.203.203 0 0 0-.19-.132.203.203 0 0 0-.19.132l-.372.993Z" fill="#fff"/></svg>
                    <span>${recommendation.sourceProductTitle}</span>
                  </a>
                </li>`;
                })
                .join('')}
            </ul>
          </details>`;
        
        const firstRecommendationsLink = shadeRecommendationsLabelSource.querySelector('details a svg');
        firstRecommendationsLink.classList.remove('opacity-0');
        firstRecommendationsLink.classList.add('opacity-100');

        activeShades = priorityAddToCartRecommendations[0].recommendedVariants;
      }

      const recommendationSelectorLinks =
        shadeRecommendationsLabelSource.querySelectorAll(
          '[data-recommendation-selector] a'
        );
      recommendationSelectorLinks.forEach((link) => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const recommendedVariants = link.getAttribute('data-recommended-variants').split(',');

          // Update the active shades
          activeShades = recommendedVariants;
          highlightShadeRecommendations(activeShades);

          const shadeRecommendationsSummary = shadeRecommendationsLabelSource.querySelector('summary span');
          const label = link.querySelector('span').innerHTML;
          shadeRecommendationsSummary.innerHTML = label;

          const shadeRecommendationSelector = shadeRecommendationsLabelSource.querySelector('[data-recommendation-selector]');
          shadeRecommendationSelector.removeAttribute('open');

          recommendationSelectorLinks.forEach((link) => {
            const svg = link.querySelector('svg');
            svg.classList.remove('opacity-100');
            svg.classList.add('opacity-0');
          });

          const svg = link.querySelector('svg');
          svg.classList.remove('opacity-0');
          svg.classList.add('opacity-100');
        });
      });

      
    } else {
      activeShades = recommendedShades[0].recommendedVariants;
    }

    highlightShadeRecommendations(activeShades);
    
  } else {
    // Hide shade recommendations label
    const currentForm = document.querySelector(`form[data-product-handle="${currentUrlLastSegment}"]`);
    if (!currentForm) return;
    const shadeRecommendationsLabel = currentForm.querySelector(`[data-shade-recommendations]`);
    if(shadeRecommendationsLabel) {
      shadeRecommendationsLabel.classList.add('hidden');

      // Remove shade recommendations from swatches
      highlightShadeRecommendations([]);
    }
  }
}

function highlightShadeRecommendations(activeShades) {
  const currentUrl = new URL(window.location.href);
  const currentUrlLastSegment = currentUrl.pathname.split('/').pop();
  const currentForm = document.querySelector(`form[data-product-handle="${currentUrlLastSegment}"]`);

  const singleProductOption = currentForm.querySelector(`.single-option-selector`);
  const shadeTitles = [];

  // Remove all shade recommendations
  const shadeRecommendations = currentForm.querySelectorAll(
    `.swatch-container.recommended-shade`
  );
  shadeRecommendations.forEach((shadeRecommendation) => {
    shadeRecommendation.classList.remove('recommended-shade');
  });

  activeShades.forEach((shade, index) => {
    const shadeTitle = singleProductOption
      .querySelector(`option[value="${shade}"]`)
      .textContent.replace(' - Sold Out', '')
      .trim();
    shadeTitles.push(shadeTitle);

    const shadeSwatch = currentForm.querySelector(
      `.swatch-container[data-value="${shadeTitle}"]`
    );
    shadeSwatch.classList.add('recommended-shade');
    // TODO: Add selection once recommendations data is finalized
    // if (index === 0) {
    //   const input = shadeSwatch.querySelector('input');
    //   input.click();
    // }
  });
}

function getShadeRecommendations() {

  // Fetch Shade Recommendations based on cart from /?section_id=cart-shade-recommendations
  const url = `/?section_id=cart-shade-recommendations`;
  return fetch(url)
    .then((response) => response.text())
    .then((html) => {
      if (!html) return false;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const sectionData = doc.querySelector('.shopify-section').innerHTML;
      if (!sectionData) return false;
      
      try {
        JSON.parse(sectionData);
      } catch(error) {
        console.warn('Shade Recommendations JSON parse error', error);
        return false;
      }
      const addToCartRecommendations = JSON.parse(sectionData);

      sessionStorage.setItem(
        'ShadeRecommendations',
        JSON.stringify(addToCartRecommendations)
      );

      applyShadeRecommendations();
      
      return true;
    })
    .catch((error) => {
      console.warn(error);
    });
}

function initYotpoLoyalty() {
  $(document).on('swell:setup', function () {
    theme.yotpoLoyaltyLoaded = true;
    updateYotpoLoyalty();
  });
}

function updateYotpoLoyalty() {
  const customerDetails = swellAPI.getCustomerDetails();

  const credit = cartDrawer.querySelector('.cart-drawer__credit');
  const creditAmountText = credit.querySelector(
    '.cart-drawer__credit-balance span'
  );
  const creditBalance = credit.querySelector('.cart-drawer__credit-balance');
  const applyCredit = credit.querySelector('.cart-drawer__credit-apply');
  const creditInfo = credit.querySelector('.cart-drawer__credit-info');

  if (!customerDetails.email || customerDetails.email === undefined) {
    creditBalance.style.display = 'none';
    applyCredit.innerHTML =
      'Sign in or create an account to see your Try Out credits';
    applyCredit.setAttribute('href', '/account/login');
    applyCredit.style.width = '100%';
    applyCredit.style.display = 'block';
    applyCredit.style.textAlign = 'center';

    credit.classList.remove('hidden');
  }

  if (!customerDetails.email || customerDetails.pointsBalance === 0) return;

  // console.log('customerDetails', customerDetails, swellAPI.getPointsInCart());

  credit.classList.remove('hidden');
  creditAmountText.innerText = customerDetails.pointsBalance;

  if (sessionStorage.getItem('credit-info')) {
    creditInfo.innerHTML = sessionStorage.getItem('credit-info');
    creditInfo.classList.remove('hidden');
    applyCredit.innerHTML = 'Update Credit';
  }

  const onSuccess = function (redemption) {
    // console.log('success', redemption);

    const creditAmountText = credit.querySelector(
      '.cart-drawer__credit-message span'
    );
    const creditCodeText = credit.querySelector(
      '.cart-drawer__credit-code span'
    );

    creditAmountText.innerText = redemption.amount;
    creditCodeText.innerText = redemption.coupon_code;

    const shopDomain = window.location.origin;
    const discountApplyURL = `${shopDomain}/discount/${redemption.coupon_code}`;

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.onload = function () {
      iframe.parentNode.removeChild(iframe);
    };
    iframe.src = discountApplyURL;
    document.body.appendChild(iframe);

    creditInfo.classList.remove('hidden');

    applyCredit.innerHTML = 'Update Credit';

    sessionStorage.setItem('yotpo-redemption', JSON.stringify(redemption));
    sessionStorage.setItem('credit-info', creditInfo.innerHTML);
  };

  const onError = function (error) {
    console.log('error', error);
  };

  // Update credit application if subotal is lower than previous credit
  let subtotal = Math.floor(theme.cartData.items_subtotal_price / 100);
  if (sessionStorage.getItem('yotpo-redemption')) {
    const redemption = JSON.parse(sessionStorage.getItem('yotpo-redemption'));
    const previousRedemptionAmount = redemption.amount;

    if (subtotal < previousRedemptionAmount) {
      applyCredit.innerHTML = '';
      swellAPI.convertPointsToDiscount(
        {amount: subtotal, delayPointDeduction: true},
        onSuccess,
        onError
      );
    }
  }

  applyCredit.addEventListener('click', (event) => {
    event.preventDefault();

    subtotal = Math.floor(theme.cartData.items_subtotal_price / 100);
    let creditTotal = customerDetails.pointsBalance;

    applyCredit.innerHTML = '';

    let pointsToApply = creditTotal;
    if (creditTotal >= subtotal) {
      pointsToApply = subtotal;
    }

    swellAPI.convertPointsToDiscount(
      {amount: pointsToApply, delayPointDeduction: true},
      onSuccess,
      onError
    );
  });
}

export function importProductRecommendations() {
  fetch('/?sections=product-upsell')
    .then(function (response) {
      return response.text();
    })
    .then(function (state) {
      const parsedState = JSON.parse(state);
      const sectionText = parsedState['product-upsell'];

      const parser = new DOMParser();
      const sectionHtml = parser
        .parseFromString(sectionText, 'text/html')
        .querySelector('.shopify-section');

      // New upsell items
      const upsellItems = Array.from(
        sectionHtml.querySelectorAll('[data-cart-dynamic-upsell]')
      ).reverse();

      const upsellItemsContainer = cartUpsell.querySelector(
        '.cart-drawer__upsell-items-container'
      );

      // remove existing section
      const oldUpsellItems = upsellItemsContainer.querySelectorAll(
        '[data-cart-dynamic-upsell]'
      );
      oldUpsellItems.forEach(function (oldItem) {
        oldItem.remove();
      });

      upsellItems.forEach(function (item) {
        upsellItemsContainer.prepend(item);

        const addToCartButton = item.querySelector('[data-variant-id]');
        const variantId = Number(addToCartButton.dataset.variantId);
        const productId = Number(item.dataset.cartUpsellItem);
        addToCartButton.addEventListener('click', function (event) {
          if (this.dataset.link) {
            window.location = this.dataset.link;
          } else {
            addItem(variantId, {}, 'cart', 'upsell');
          }

          const upsellItem = this.closest('.cart-drawer__upsell-item');
          const upsellTitle = upsellItem.querySelector(
            '.product-single__title'
          ).innerText;

          event.preventDefault();
        });

        const links = item.querySelectorAll('a');
        const productLink = links[0].href;
        links.forEach(function (link) {
          link.addEventListener('click', function (event) {
            event.preventDefault();
            window.location = productLink;
          });
        });
      });

      const upsellProductName = cartUpsell.querySelector(
        '[data-upsell-product-name]'
      );
      const upsellMessage = sectionHtml.querySelector(
        '[data-recommendations-label]'
      );
      if (upsellItems.length > 0) {
        upsellProductName.innerHTML = '';
        upsellProductName.append(upsellMessage);
      } else {
        upsellProductName.innerHTML = '';
      }

      const upsellHeading = cartDrawer.querySelector('[data-upsell-heading]');
      const upsellHeadingSets = cartDrawer.querySelector(
        '[data-upsell-heading-sets]'
      );
      const lastProductType = upsellMessage.dataset.lastProductType
        .replace(/\s+/g, '-')
        .toLowerCase();

      if (lastProductType == 'set' || lastProductType == 'bundle') {
        upsellHeading.classList.remove('hidden');
        upsellHeadingSets.classList.add('hidden');
        upsellProductName.classList.remove('hidden');
      } else {
        upsellHeading.classList.add('hidden');
        upsellHeadingSets.classList.remove('hidden');
        upsellProductName.classList.add('hidden');
        upsellProductName.classList.add('hidden');
      }

      updateUpsellCards(window.theme.cartData);

      upsellItemsContainer.scrollLeft = 0;
    })
    .catch(function (err) {
      console.warn('Product Recommendations - Something went wrong', err);
    });
}

export function refreshCart(isAddToCart) {
  cartApi.getState().then((state) => {
    const cartItem = cartDrawer.querySelectorAll(selectors.cartItem);
    cartItem.forEach(function (item) {
      item.remove();
    });

    // reset bundle products
    bundleProducts = [];
    state.items.forEach(function (item) {
      renderItem(item);
    });
    updateCart(state);
    setCartCount(state);
    updateSampleCards(state);
    //updateUpsellCards(state);

    if (isAddToCart) {
      Klaviyo.trackAddToCart(state);
    }
  });
}

function showGWPOptions(productId, code, notDiscountCode) {
  let gwpVariants;

  if (notDiscountCode === undefined) {
    const cartDrawerGWPProduct = document.querySelector(
      '[data-cart-drawer-gwp-product]'
    );
    const cartDrawerDiscountContainer = cartDrawer.querySelector(
      '.cart-drawer__discount'
    );
    const gwpClone = cartDrawerGWPProduct.cloneNode(true);
    gwpClone.classList.remove('hidden');
    cartDrawerDiscountContainer.appendChild(gwpClone);

    gwpVariants = gwpClone.querySelectorAll('[data-gwp-variant]');

    // Scroll to top
    cartDrawerDiscountContainer.scrollIntoView({
      behavior: 'smooth',
    });

    const notDiscountCode = false;
  } else {
    const cartDrawerProgressAction = cartDrawer.querySelector(
      '.cart-drawer__progress-action'
    );
    const gwpCartProduct = cartDrawer.querySelector(
      `[data-cart-drawer-gwp-cart-product="${productId}"]`
    );
    cartDrawerProgressAction.classList.remove('hidden');
    gwpCartProduct.classList.remove('hidden');
    gwpVariants = gwpCartProduct.querySelectorAll('[data-gwp-variant]');
  }

  gwpVariants.forEach((item) => {
    const variantId = Number(item.dataset.variantId);
    item.addEventListener('click', (evt) => {
      addGWPItem(variantId, code, notDiscountCode);

      const gwpCartProduct = item.closest(
        '[data-cart-drawer-gwp-cart-product]'
      );
      const cartDrawerProgressAction = cartDrawer.querySelector(
        '.cart-drawer__progress-action'
      );

      if (gwpCartProduct) {
        cartDrawerProgressAction.classList.add('hidden');
        gwpCartProduct.classList.add('hidden');
      }
      evt.preventDefault();
    });
  });
}

function addGWPItem(addVariantId, code, notDiscountCode) {
  const options = {
    quantity: 1,
    properties: {
      _gwp: code,
    },
  };
  window.theme.discountUpdateRemoveGWP = true;
  window.theme.discountGWPAddTime = new Date();
  // console.log('check gwp', code);

  if (notDiscountCode == true) {
    options.properties._promo = code;
    options.properties._bundle_title = code;
    options.properties._d = 0;

    // console.log('validate SPECIAL - ', options);
  }

  cartApi.addItem(addVariantId, options).then((item) => {
    // Premature update cart button count
    // Potential issues will be resolved with cart status fetch
    let quantity;
    if (options === undefined) {
      quantity = 1;
    } else {
      quantity = options.quantity;
    }
    // Increase Cart Count
    increaseCartCount(quantity);

    // Render new item
    if (item) {
      renderItem(item, {
        prepend: true,
      });
    }

    const itemKey = item.key;
    const thisCartItem = cartDrawer.querySelector(
      `[data-cart-item="${itemKey}"]`
    );

    if (thisCartItem) {
      delete thisCartItem.dataset.itemPending;
    }

    const cartDrawerGWPProduct = cartDrawer.querySelector(
      '[data-cart-drawer-gwp-product]'
    );
    if (cartDrawerGWPProduct) {
      cartDrawerGWPProduct.remove();
    }

    updateCart();
    refreshCart();

    gtmTrackCartAddToCart(item, 'GWP');

    window.theme.discountUpdateRemoveGWP = false;
  });
}

function addItemEvents(item) {
  // Remove items
  const remove = item.querySelector(selectors.cartRemove);

  if (remove) {
    remove.addEventListener('click', function (event) {
      event.preventDefault();
      const cartItem = event.target.closest(selectors.cartItem);

      if (cartItem.dataset.cartItemGroup) {
        const groupCartItems = cartItems.querySelectorAll(
          `[data-cart-item-group="${cartItem.dataset.cartItemGroup}"]`
        );
        updateCart();

        let items = {};

        groupCartItems.forEach(function (item) {
          const itemKey = item.dataset.cartItem;
          items[itemKey] = 0;
        });

        removeItems(items);
      } else {
        updateCart();

        const itemKey = this.dataset.cartRemove;
        cartApi.removeItem(itemKey).then((state) => {
          gtmTrackRemoveItem(itemKey);
          updateCart(state);
          getShadeRecommendations();
        });
        item.remove();
      }
    });

    // Quantity - Remove
    const itemRemove = item.querySelector(selectors.cartRemoveItem);
    if (itemRemove) {
      itemRemove.addEventListener('click', function (event) {
        event.preventDefault();

        const cartItem = event.target.closest('[data-cart-item]');
        const variantId = Number(cartItem.dataset.cartVariantId);
        const cartQuantityItem = cartItem.querySelector(
          selectors.cartQuantityItem
        );
        let quantity = cartQuantityItem.value;
        quantity = quantity - 1;

        const itemKey = cartItem.dataset.cartItem;

        cartItem.dataset.itemPending = true;

        if (quantity > 0) {
          cartQuantityItem.value = quantity;
          updateCart();
          cartApi.updateItem(itemKey, {quantity}).then((state) => {
            const cartItem = cartDrawer.querySelectorAll(selectors.cartItem);
            cartItem.forEach(function (item) {
              item.remove();
            });
            bundleProducts = [];
            state.items.forEach(function (item) {
              renderItem(item);
            });
            updateCart(state);
            setCartCount(state);
          });
        } else {
          cartDrawer.dataset.cartPending = true;
          cartApi.removeItem(itemKey).then((state) => {
            getShadeRecommendations();
            gtmTrackRemoveItem(itemKey);
            updateCart(state);
          });
          item.remove();
        }
      });
    }

    // Quantity - Add
    const itemAdd = item.querySelector(selectors.cartAddItem);
    if (itemAdd) {
      itemAdd.addEventListener('click', function (event) {
        event.preventDefault();

        const cartItem = event.target.closest('[data-cart-item]');
        const variantId = Number(cartItem.dataset.cartVariantId);

        cartItem.dataset.itemPending = true;
        cartDrawer.dataset.cartPending = true;
        // addItem(variantId);
        const cartQuantityItem = cartItem.querySelector(
          selectors.cartQuantityItem
        );
        let quantity = Number(cartQuantityItem.value);
        quantity = quantity + 1;
        const itemKey = cartItem.dataset.cartItem;

        cartApi.updateItem(itemKey, {quantity}).then((state) => {
          const cartItem = cartDrawer.querySelectorAll(selectors.cartItem);
          cartItem.forEach(function (item) {
            item.remove();
          });
          bundleProducts = [];
          state.items.forEach(function (item) {
            renderItem(item);
          });
          updateCart(state);
          setCartCount(state);
        });
      });
    }
  }

  const cartGiftMessage = item.querySelector(selectors.cartGiftMessage);
  if (cartGiftMessage) {
    const charactersLeft = item.querySelector(selectors.cartCharactersLeft);

    let charactersRemaining;
    cartGiftMessage.addEventListener(
      'keyup',
      function (event) {
        charactersRemaining = countCharacters(event);
        const message = `${charactersRemaining} Characters`;

        charactersLeft.innerHTML = message;

        if (charactersRemaining <= 0) {
          charactersLeft.classList.add('text-red-700');
        } else {
          charactersLeft.classList.remove('text-red-700');
        }
      },
      false
    );

    cartGiftMessage.addEventListener(
      'blur',
      function (event) {
        const value = event.target.value.trim();
        const cartItem = event.target.closest(selectors.cartItem);
        const removeLink = cartItem.querySelector(selectors.cartRemove);
        const key = cartItem.dataset.cartItem;
        const id = cartItem.dataset.cartId;

        cart
          .updateItem(key, {
            quantity: 1,
            properties: {
              'Gift Message': value,
            },
          })
          .then((state) => {
            state.items.forEach(function (item) {
              if (item.id == id) {
                const updatedGiftWrapItem = cartDrawer.querySelector(
                  `[data-cart-id="${id}"]`
                );
                updatedGiftWrapItem.dataset.cartItem = item.key;
                removeLink.dataset.cartRemove = item.key;
              }
            });
            updateCart(state);
          });
      },
      false
    );
  }
}

function countCharacters(event) {
  const charcterLimit = 108;

  const textarea = event.target;
  const value = textarea.value;
  const counter = charcterLimit - value.length;

  return counter;
}

// Calling updateCart without a state puts the cart in a pending mode
// Calling again with a state resolves the pending status
export function updateCart(cartState) {
  // console.log('updateCart', cartState);
  if (!cartState) {
    cartDrawer.dataset.cartPending = true;
    return;
  }
  // Add cart data to global variable
  window.theme.cartData = cartState;
  gtmTrackCart();

  if (cartDrawer.dataset.cartPending) {
    delete cartDrawer.dataset.cartPending;
  }

  if (cartState.item_count > 0) {
    cartDrawerFooter.classList.remove('hidden');
    cartDrawerBottom.classList.remove('hidden');
    cartDrawerThreshold.classList.remove('hidden');
    cartDrawerEmpty.classList.add('hidden');
    cartUpsell.classList.remove('hidden');
  } else {
    cartDrawerFooter.classList.add('hidden');
    cartDrawerBottom.classList.add('hidden');
    cartDrawerThreshold.classList.add('hidden');
    cartDrawerEmpty.classList.remove('hidden');
    cartUpsell.classList.add('hidden');
    if (cartFreeGift) {
      cartFreeGift.classList.add('hidden');
    }
  }

  // If there are one or more free items in the cart, clear it
  if (cartState.item_count > 0 && cartState.items_subtotal_price == 0) {
    const cartItems = cartDrawer.querySelector(selectors.cartItems);

    while (cartItems.firstChild) {
      cartItems.removeChild(cartItems.firstChild);
    }
    cartApi.clearItems().then((state) => {
      updateCart(state);
    });
    return;
  }

  let freeSampleItem = false;
  let giftWrapItem = false;
  cartState.items.forEach(function (item) {
    if (upsellItems.includes(item.id)) {
      freeSampleItem = true;
    }

    if (item.title === 'Gift Wrapping') {
      giftWrapItem = true;
    }
  });

  if (!giftWrapItem && cartState.item_count > 0) {
    cartGiftWrap.classList.remove('hidden');
  }

  if (!freeSampleItem && cartState.item_count > 0) {
    cartUpsell.classList.remove('hidden');
  }

  let freeGiftItem = false;
  cartState.items.forEach(function (item) {
    if (
      item.properties !== null &&
      item.properties._cyber_monday !== undefined
    ) {
      freeGiftItem = true;
    }
  });

  if (cartFreeGift && !freeGiftItem && cartState.item_count > 0) {
    cartFreeGift.classList.remove('hidden');
  }

  // Update Cart Total
  const cartSubtotal = cartDrawer.querySelector(selectors.cartSubtotal);
  const subtotal = Number(cartState.items_subtotal_price);
  const subtotalMoney = theme.formatMoney(subtotal, theme.moneyFormat);
  cartSubtotal.innerText = subtotalMoney;

  // Update Cart Original Total
  const cartSubtotalOriginal = cartDrawer.querySelector(
    selectors.cartSubtotalOriginal
  );
  const subtotalOriginal = Number(cartState.original_total_price);
  const subtotalOriginalMoney = theme.formatMoney(
    subtotalOriginal,
    theme.moneyFormat
  );
  cartSubtotalOriginal.innerText = subtotalOriginalMoney;
  if (subtotal < subtotalOriginal) {
    cartSubtotal.classList.add('text-accent');
    cartSubtotalOriginal.classList.remove('hidden');
  } else {
    cartSubtotal.classList.remove('text-accent');
    cartSubtotalOriginal.classList.add('hidden');
  }

  // Update AfterPay
  const cartAfterpay = cartDrawer.querySelector(selectors.cartAfterpay);
  if (cartAfterpay) {
    const subtotalWithCents = subtotal / 100;
    cartAfterpay.dataset.amount = subtotalWithCents;
  }

  // Update header cart count
  setCartCount(cartState);
  updateSampleCards(cartState);
  //updateUpsellCards(cartState);

  if (cartState) {
    importProductRecommendations();
  }

  updateProgressBars(cartState);
  autoAddProduct(cartState);

  if (theme.yotpoLoyaltyLoaded) {
    updateYotpoLoyalty();
  }
}

window.updateCart = function () {
  updateCart(theme.cartData);
};

async function updateProgressBars(cartState) {
  if (!cartState) return;

  let totalPrice = cartState.total_price;
  const progressBars = cartDrawer.querySelectorAll(
    '.cart-drawer__progress-bar'
  );
  const progressMessages = cartDrawer.querySelectorAll(
    '.cart-drawer__progress-message'
  );

  const containsTakeTheSiteHome = cartState.items.filter(
    (item) => item.handle === 'take-the-site-home-kit'
  );
  /*
    Loop through each bar
    - if totalPrice is inside the range change
  */
  let firstActiveProgressBar = false;
  let firstActiveProgressMessage = false;
  progressBars.forEach(function (bar, index) {
    const innerBar = bar.querySelector('.cart-drawer__progress-bar-inner');
    const cartTotalOffset = totalPrice - bar.dataset.thresholdStart;
    const thresholdEndOffset =
      bar.dataset.thresholdEnd - bar.dataset.thresholdStart;
    let percentage = Math.floor((cartTotalOffset / thresholdEndOffset) * 100);

    const progressMoney = (
      (bar.dataset.thresholdEnd - totalPrice) /
      100
    ).toFixed(2);

    if (containsTakeTheSiteHome.length > 0 && bar.dataset.action === 'false') {
      percentage = 100;
    }

    if (percentage > 100) {
      percentage = 100;
    } else if (percentage < 0) {
      percentage = 0;
    }
    if (percentage < 100 && !firstActiveProgressBar) {
      bar.dataset.active = true;
      firstActiveProgressBar = true;
    } else {
      bar.dataset.active = false;
    }

    sleep(300).then(() => {
      bar.dataset.percent = percentage;
      innerBar.style.width = `${percentage}%`;
    });

    const message = progressMessages[index];
    const messageAction = JSON.parse(message.dataset.action);
    const messageProgressMoney = message.querySelector('[data-progress-money]');
    const inprogressText = message.querySelector(
      '[data-progress-inprogress-message]'
    );
    const completeText = message.querySelector(
      '[data-progress-complete-message]'
    );

    if (percentage == 100) {
      message.dataset.complete = 'true';
      inprogressText.classList.add('hidden');
      completeText.classList.remove('hidden');

      if (!messageAction) {
        cartSubheadingDefault.innerHTML = completeText.innerText;
      }
    } else if (!firstActiveProgressMessage) {
      message.dataset.complete = 'progress';
      firstActiveProgressMessage = true;
      if (messageProgressMoney) {
        messageProgressMoney.innerText = progressMoney;
      }
      inprogressText.classList.remove('hidden');
      completeText.classList.add('hidden');

      if (!messageAction) {
        cartSubheadingDefault.innerHTML = cartSubheadingDefaultText;
      }
    } else {
      message.dataset.complete = 'false';
      inprogressText.classList.remove('hidden');
      completeText.classList.add('hidden');

      if (!messageAction) {
        cartSubheadingDefault.innerHTML = cartSubheadingDefaultText;
      }
    }
  });

  validateProgressActions(cartState);
}

function validateProgressActions(cartState) {
  let totalPrice = cartState.total_price / 100;
  const cartGwp = window.theme.cartGwp;

  // Check sessionStorage for utmParams
  const utmParams = JSON.parse(sessionStorage.getItem('utmParams'));
  let utmSource = false;
  let utmMedium = false;
  let utmCampaign = false;
  let utmContent = false;
  let utmTerm = false;
  if (utmParams) {
    // If the utm params exist, set them to variables otherwise set them to false
    utmSource = utmParams.utm_source;
    utmMedium = utmParams.utm_medium;
    utmCampaign = utmParams.utm_campaign;
    utmContent = utmParams.utm_content;
    utmTerm = utmParams.utm_term;
  }

  const shippablePaidItems = window.theme.cartData.items.filter(
    (item) => item.line_price > 0 && item.requires_shipping === true
  );

  cartGwp.forEach((gwp) => {
    const utmCampaignMatch = () => {
      if (gwp.utmCampaign) {
        if (utmCampaign) {
          return gwp.utmCampaign === utmCampaign;
        } else {
          return false;
        }
      } else {
        return true;
      }
    };

    // Check threshold against total price
    if (totalPrice >= gwp.priceThreshold && shippablePaidItems.length > 0) {
      // Filter by _gwp and the _promo name match (for duplicate gwp products)
      const filterSpecificGwp = window.theme.cartData.items.filter(
        (item) =>
          item.properties['_gwp'] &&
          item.properties['_promo'] === gwp.promoTitle
      );

      if (filterSpecificGwp.length < 1 && utmCampaignMatch()) {
        if (gwp.variantId !== 0) {
          addGWPItem(gwp.variantId, gwp.promoTitle, true);
        } else {
          if (gwp.primary) {
            showGWPOptions(gwp.productId, gwp.promoTitle, true);
          }
        }
      }
    } else {
      // Look up item to remove and clean it up
      const itemIndexToRemove = window.theme.cartData.items.findIndex(
        (item) => item.properties['_gwp'] && item.product_id === gwp.productId
      );

      if (itemIndexToRemove != -1) {
        const keyToRemove = window.theme.cartData.items[itemIndexToRemove].key;
        window.theme.cartData.items.splice(itemIndexToRemove, 1);
        window.theme.discountUpdateRemoveGWP = true;
        cartApi.removeItem(keyToRemove).then((state) => {
          gtmTrackRemoveItem(keyToRemove);
          updateCart(state);
          refreshCart();
          window.theme.discountUpdateRemoveGWP = false;
        });
      }
    }
  });
}

function trueTotalPrice(cartState) {
  console.log('true - calculate the true total price skipping');
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function increaseCartCount(quantity) {
  const currentCartCount = Number(cartButton.dataset.cartCount);
  const newCartCount = currentCartCount + quantity;

  cartButton.dataset.cartCount = newCartCount;
  cartButtonText.innerText = newCartCount;
}

function setCartCount(data) {
  let cartCount = 0;
  let bundles = [];

  data.items.forEach(function (item) {
    let itemCount = 0;
    if (bundles.includes(item.properties._bundle_id)) {
      return;
    }

    if (item.properties._bundle_id) {
      bundles.push(item.properties._bundle_id);
      itemCount = item.quantity;
    } else {
      itemCount = item.quantity;
    }
    cartCount += itemCount;
  });

  cartButton.dataset.cartCount = cartCount;
  cartButtonText.innerText = cartCount;

  let cartCountMessageText = '';
  if (cartCount == 1) {
    cartCountMessageText = `${cartCount} Item`;
  } else if (cartCount > 1) {
    cartCountMessageText = `${cartCount} Items`;
  } else {
    cartCountMessageText = '0 Items';
  }
  cartCountMessage.innerText = cartCountMessageText;

  // Update cartSubheadingDefault
  const cartSubheadingDefault = cartDrawer.querySelector(
    selectors.cartSubheadingDefault
  );
  const cartSubheadingMinimum = cartDrawer.querySelector(
    selectors.cartSubheadingMinimum
  );
  const cartShippingAlert = cartDrawer.querySelector(
    selectors.cartShippingAlert
  );
  const cartMinimum = cartSubheadingMinimum.dataset.cartSubheadingMinimum * 100;

  if (data.total_price >= cartMinimum) {
    cartSubheadingDefault.classList.remove('invisible');
    cartSubheadingMinimum.classList.add('hidden');
    cartShippingAlert.classList.add('hidden');
  } else {
    cartSubheadingDefault.classList.add('invisible');
    cartSubheadingMinimum.classList.add('hidden');
    cartShippingAlert.classList.remove('hidden');

    const minimumPrice = cartSubheadingMinimum.querySelector('span');
    const remaining = (cartMinimum - data.total_price) / 100;
    minimumPrice.innerHTML = remaining;
  }
}

function renderItem(item, options) {
  options = options || {};

  const itemLinePrice = Number(item.line_price);
  const itemPrice = theme.formatMoney(item.price, theme.moneyFormat);
  const linePrice = theme.formatMoney(item.line_price, theme.moneyFormat);
  const itemOriginalLinePrice = Number(item.original_line_price);
  const originalLinePrice = theme.formatMoney(
    itemOriginalLinePrice,
    theme.moneyFormat
  );

  let bundlePrice, bundleOriginalPrice, bundleUrl, bundleImage, bundleTitle;

  const itemKey = item.key;
  const itemQuantity = item.quantity;
  let itemGroup = '';
  if (item.properties !== null && item.properties._group !== undefined) {
    itemGroup = item.properties._group;
  } else if (
    item.properties !== null &&
    item.properties._bundle_id !== undefined
  ) {
    itemGroup = item.properties._bundle_id;
  }

  if (itemGroup !== '') {
    if (bundleProducts.includes(item.properties._bundle_id)) {
      hideBundle = true;
    } else {
      hideBundle = false;
    }

    bundleUrl = item.properties._bundle_url;
    bundleImage = item.properties._bundle_image;
    bundleTitle = item.properties._bundle_title;
    bundleProducts.push(itemGroup);
    
    // let sitewideCartDiscount = 1;
    // if (item.properties && item.properties['_sitewide_sale']) {
    //   if (item.properties['_sitewide_sale'].includes('15%')) {
    //     sitewideCartDiscount = 0.85;
    //   } else if (item.properties['_sitewide_sale'].includes('20%')) {
    //     sitewideCartDiscount = 0.8;
    //   } else if (item.properties['_sitewide_sale'].includes('25%')) {
    //     sitewideCartDiscount = 0.75;
    //   }
    // }

    bundlePrice = theme.formatMoney(
      item.properties._bundle_price * item.quantity,
      theme.moneyFormat
    );

    bundleOriginalPrice = theme.formatMoney(
      item.properties._bundle_compare_at_price * item.quantity,
      theme.moneyFormat
    );

  }

  let itemDiscountTitle = '';
  if (item.discounts.length > 0 && item.discounts[0].title !== undefined) {
    itemDiscountTitle = item.discounts[0].title;
  }

  // Check for key to update quantity and total
  let cartItem = cartDrawer.querySelector(`[data-cart-item="${itemKey}"]`);
  if (cartItem) {
    if (itemLinePrice > 0) {
      if (cartItem.querySelector('[data-cart-line-price-final]')) {
        cartItem.querySelector('[data-cart-line-price-final]').innerText =
          linePrice;
      }
      if (cartItem.querySelector('[data-cart-line-price-original]')) {
        cartItem.querySelector('[data-cart-line-price-original]').innerText =
          originalLinePrice;
      }
    }
    document.getElementById(`updates_${item.key}`).value = itemQuantity;

    // Only allow one free item
    const variantId = Number(item.variant_id);
    const variantKey = item.key;
    let upsellRemove;

    // Remove more than one of same free item
    if (upsellRemove !== undefined) {
      const quantity = 1;
      cartApi.updateItem(upsellRemove, {quantity}).then((state) => {
        state.items.forEach(function (item) {
          renderItem(item);
        });
        updateCart(state);
      });
    }
    return;
  }

  let gwpItem = false;
  if (item.properties && item.properties['_gwp']) {
    gwpItem = true;
  }

  let siteWideSaleItem = false;
  // if (item.properties && item.properties['_sitewide_sale']) {
  //   siteWideSaleItem = true;
  // }

  let siteWideSaleSpecialItem = false;
  // if (item.properties && item.properties['_sitewide_sale_special']) {
  //   siteWideSaleSpecialItem = true;
  // }
  // Otherwise create a new item
  const itemHTML = `
    <div class="relative border bg-white rounded-sm p-2 flex items-center flex-wrap mb-2${
      item.properties !== null &&
      item.properties.hasOwnProperty('_bundle_child') &&
      item.properties._bundle_child
        ? ` hidden`
        : ``
    }"
      data-cart-item="${item.key}"
      data-cart-id="${item.id}"
      data-cart-variant-id="${item.variant_id}"
      data-cart-product-type="${item.product_type}"
      ${itemGroup !== '' ? `data-cart-item-group="${itemGroup}"` : ``}
      data-cart-template>
      <div class="w-1/5">
        <a href="${
          item.url
        }" class="responsive-image__wrapper " style="padding-top:100.0%;">
          <img loading="lazy" alt="${item.product_title + ' - ' + item.variant_title}" src="${
            item.featured_image.url
              ? item.featured_image.url
                  .replace('.jpg', '_70x.jpg')
                  .replace('.png', '_70x.png')
              : `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mN88R8AAtUB6S/lfiQAAAAASUVORK5CYII=`
          }">
        </a>
      </div>
      <div class="${
        itemLinePrice == 0 || gwpItem ? `w-4/5` : `w-3/5`
      } px-2 font-display leading-tight text-sm">
        <div class="font-bold" data-item-product-title><a href="${item.url}">${
    item.product_title
  }</a></div>
        ${
          item.variant_title && item.product_type !== 'set'
            ? `<p class="text-xs" data-item-variant-title data-type="${item.product_type}">${item.variant_title}</p>`
            : ``
        }
        ${
          item.title == 'Gift Wrapping'
            ? `<p class="text-xs" data-item-variant-title class="italic text-accent">Samples will not be gift wrapped</p>`
            : ``
        }
        ${
          item.selling_plan_allocation
            ? `<p class="text-xs" data-item-variant-title class="italic text-accent text-xs">${item.selling_plan_allocation.selling_plan.name}</p>`
            : ``
        }
        ${
          itemLinePrice > 0 && !gwpItem
            ? `<p class="inline-block relative pt-1" data-cart-line-price>
              ${
                itemLinePrice < itemOriginalLinePrice
                  ? `<span data-cart-line-price-final class="text-accent font-bold">${linePrice}</span>
                     <span class="sr-only">On sale from: </span>
                     <span data-cart-line-price-original class="pl-1 text-gray-700 text-xs line-through">${originalLinePrice}</span>`
                  : `<span data-cart-line-price-final class="font-bold">${linePrice}</span>`
              }
              ${
                siteWideSaleSpecialItem
                  ? `<span class="block text-xs text-accent">${item.properties._sitewide_sale_special}</span>`
                  : ``
              }
              ${
                siteWideSaleItem
                  ? `<span class="block text-xs text-accent">${item.properties._sitewide_sale}</span>`
                  : ``
              }
              </p>
              ${
                item.product_description.includes('final sale')
                  ? `<span class="text-accent text-xs">Final Sale</span>`
                  : ``
              }
              `
            : `${
                itemDiscountTitle !== '' && !gwpItem
                  ? `<p class="inline-block relative" data-cart-line-price><span class="text-accent">${itemDiscountTitle}</span> <span class="text-gray-700 text-xs line-through">${originalLinePrice}</span></p>`
                  : `<p class="inline-block relative" data-cart-line-price><span class="text-accent font-bold">Free Gift</span></p>`
              }
              `
        }
      </div>
      <div class="${
        itemLinePrice == 0 || gwpItem ? `hidden` : `w-1/5`
      } space-y-2">
        ${
          (itemPrice == 0 && itemDiscountTitle !== '') || gwpItem
            ? ``
            : `<button class="block text-sm font-display text-gray-600 ml-auto" data-cart-remove="${item.key}">
              <small>Remove</small>
              <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" class="icon" viewBox="0 0 20 20"><path d="M15.89 14.696l-4.734-4.734 4.717-4.717c.4-.4.37-1.085-.03-1.485s-1.085-.43-1.485-.03L9.641 8.447 4.97 3.776c-.4-.4-1.085-.37-1.485.03s-.43 1.085-.03 1.485l4.671 4.671-4.688 4.688c-.4.4-.37 1.085.03 1.485s1.085.43 1.485.03l4.688-4.687 4.734 4.734c.4.4 1.085.37 1.485-.03s.43-1.085.03-1.485z"/></svg>
            </button>`
        }
        <div class="cart-drawer__quanity flex flex-initial w-full bg-white border border-gray-400 rounded leading-tight ${
          itemPrice == 0 || item.title == 'Gift Wrapping' ? `hidden` : ``
        }"
          data-item-quantity>
          <label class="sr-only" for="updates_${item.key}">Quantity</label>
          <button class="flex-1"
            ${
              itemGroup !== '' || item.product_type == 'sample'
                ? `disabled`
                : ``
            }
            data-item-quantity-remove
            aria-label="decrease quantity by 1">
            <span aria-hidden>-</span>
          </button>
          <input type="number"
            name="updates[]" id="updates_${
              item.key
            }" value="${itemQuantity}" min="1" pattern="[0-9]*"
            data-item-quantity-input
            disabled
            class="cart-drawer__input cart-drawer__input--quantity appearance-none px-0 py-1 text-center w-8" data-quantity-input
          >
          <button class="flex-1"
            ${
              itemGroup !== '' || item.product_type == 'sample'
                ? `disabled`
                : ``
            }
            data-item-quantity-add
            aria-label="increase quantity by 1">
            <span aria-hidden>+</span>
          </button>
        </div>
      </div>
      ${
        item.title == 'Gift Wrapping'
          ? `<div class="w-full border-top">
            <div class="flex justify-between">
              <label for="Gift-Message" class="text-xs block">Gift Message</label>
              <span data-characters-left class="text-xs font-display text-gray-700 p-2">${
                item.properties && item.properties['Gift Message'] !== undefined
                  ? `${108 - item.properties['Gift Message'].length} Characters`
                  : `108 Characters`
              }</span>
            </div>
            <textarea name="gift-message" id="Gift-Message" maxlength="108" data-cart-gift-message class="w-full bg-white p-2 border border-gray-400 rounded leading-tight" rows="4">${
              item.properties && item.properties['Gift Message'] !== undefined
                ? `${item.properties['Gift Message']}`
                : ``
            }</textarea>
          </div>`
          : ``
      }
    </div>
  `;

  const itemBundleHTML = `
      <div class="relative border bg-white rounded-sm p-2 flex items-center flex-wrap mb-2 ${
        hideBundle ? `hidden` : ``
      }"
        data-cart-item="${item.key}"
        data-cart-id="${item.id}"
        data-cart-variant-id="${item.variant_id}"
        data-cart-product-type="${item.product_type}"
        ${itemGroup !== '' ? `data-cart-item-group="${itemGroup}"` : ``}
        data-cart-template>
        <div class="w-1/5">
          <a href="${bundleUrl}" class="responsive-image__wrapper" style="padding-top:100.0%;">
            <img loading="lazy" alt="${bundleTitle}" src="${bundleImage}">
          </a>
        </div>
        <div class="flex-auto w-3/5 px-2 font-display leading-tight text-sm" style="max-width: 246px;">
          <div class="font-bold" data-item-product-title><a href="${bundleUrl}">${bundleTitle}</a></div>
          <p class="inline-block relative pt-1" data-cart-line-price>
            ${
              Number(item.properties._bundle_price) <
              Number(item.properties._bundle_compare_at_price)
                ? `<span data-cart-line-price-final class="text-accent font-bold">${bundlePrice}</span>
                   <span class="sr-only">On sale from: </span>
                   <span data-cart-line-price-original class="text-gray-700 text-xs line-through">${bundleOriginalPrice}</span>`
                : `<span data-cart-line-price-final class="font-bold">${bundlePrice}...</span>`
            }
            ${
              siteWideSaleSpecialItem
                ? `<span class="block text-xs text-accent">${item.properties._sitewide_sale_special}</span>`
                : ``
            }
            ${
              siteWideSaleItem
                ? `<span class="block text-xs text-accent">${item.properties._sitewide_sale}</span>`
                : ``
            }
          </p>
        </div>
        <div class="flex-none w-1/5">
          <button class="block text-sm font-display ml-auto text-gray-600" data-cart-remove="${
            item.key
          }">
            <small>Remove</small>
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" class="icon" viewBox="0 0 20 20"><path d="M15.89 14.696l-4.734-4.734 4.717-4.717c.4-.4.37-1.085-.03-1.485s-1.085-.43-1.485-.03L9.641 8.447 4.97 3.776c-.4-.4-1.085-.37-1.485.03s-.43 1.085-.03 1.485l4.671 4.671-4.688 4.688c-.4.4-.37 1.085.03 1.485s1.085.43 1.485.03l4.688-4.687 4.734 4.734c.4.4 1.085.37 1.485-.03s.43-1.085.03-1.485z"/></svg>
          </button>
          <div class="cart-drawer__quanity flex flex-initial w-8 ml-auto bg-white border border-gray-400 rounded leading-tight"
            data-item-quantity>
            <label class="sr-only" for="updates_${item.key}">Quantity</label>
            <input type="number"
              name="updates[]" id="updates_${
                item.key
              }" value="${itemQuantity}" min="1" pattern="[0-9]*"
              data-item-quantity-input
              disabled
              class="cart-drawer__input cart-drawer__input--quantity appearance-none px-0 py-1 text-center w-8" data-quantity-input
            >
          </div>
        </div>
        <div class="w-full border-t mt-2 pt-2 border-gray-200">
          <div class="grid grid-cols-2">
            ${
              item.properties !== null &&
              item.properties.hasOwnProperty('_bundle_products')
                ? `${item.properties._bundle_products
                    .map((item, i) =>
                      `
                      ${
                        item.title.includes('Box')
                          ? ``
                          : `<div class="flex items-center pb-2">
                          <div class="w-1/5">
                            <img src="${item.image}" alt="${item.title}">
                          </div>
                          <div class="w-4/5 font-display leading-tight text-sm">
                            <div class="font-bold line-clamp-1">${item.title}</div>
                            <div class="line-clamp-1">${item.shade}</div>
                          </div>
                        </div>`
                      }
                      `.trim()
                    )
                    .join('')}`
                : ``
            }

          </div>
        </div>
      </div>
  `;
  const newCartItem = document.createElement('div');
  const cartItems = cartDrawer.querySelector(selectors.cartItems);

  if (item.properties !== null) {
    if (
      item.properties.hasOwnProperty('_bundle_id') &&
      item.properties._bundle_id
    ) {
      newCartItem.innerHTML = itemBundleHTML;
    } else {
      newCartItem.innerHTML = itemHTML;
    }
  } else {
    newCartItem.innerHTML = itemHTML;
  }

  if (options.prepend) {
    cartItems.prepend(newCartItem);
  } else {
    cartItems.append(newCartItem);
  }

  addItemEvents(newCartItem);
}

export function openCartDrawer() {
  backdrop.classList.remove('hidden');

  // set tabindex to 0 for all focusable elements
  cartDrawerFocusable.forEach(function (element) {
    element.setAttribute('tabindex', '0');
  });
  cartDrawer.setAttribute('tabindex', '0');
  cartDrawer.setAttribute('aria-hidden', 'false');

  // console.log('trap focus');
  cartTrapFocus.activate();

  document
    .querySelector(selectors.cartDrawer)
    .setAttribute('data-expanded', true);

  // GTM event
  gtmTrackCart();
}

export function closeCartDrawer() {
  backdrop.classList.add('hidden');

  // console.log('remove focus');
  cartTrapFocus.deactivate();

  document
    .querySelector(selectors.cartDrawer)
    .setAttribute('data-expanded', false);
  
  // If #cart is in the URL change it to #closed
  if (window.location.hash === '#cart') {
    window.location.hash = '#closed';
  }

  cartDrawerFocusable.forEach(function (element) {
    element.setAttribute('tabindex', '-1');
  });
  cartDrawer.setAttribute('tabindex', '-1');
  cartDrawer.setAttribute('aria-hidden', 'true');
}

function gtmTrackCart() {
  const cartItems = theme.cartData.items.map((item, idx) => {
    return {
      position: idx,
      id: item.sku,
      productId: item.product_id,
      variantId: item.variant_id,
      shopifyId: 'shopify_US_' + item.product_id + '_' + item.variant_id,
      name: item.product_title.replace("'", ''),
      quantity: item.quantity,
      price: item.price / 100,
      brand: item.vendor.replace("'", ''),
      variant: item.variant_title,
    };
  });

  if (theme.cartData.items.length) {
    const cartItemSkus =
      theme.cartData.items.length === 1
        ? theme.cartData.items[0].sku
        : theme.cartData.items.map((item) => {
            return item.sku;
          });

    const shopifyCartItemSkus =
      theme.cartData.items.length === 1
        ? 'shopify_US_' +
          theme.cartData.items[0].product_id +
          '_' +
          theme.cartData.items[0].variant_id
        : theme.cartData.items.map((item) => {
            return 'shopify_US_' + item.product_id + '_' + item.variant_id;
          });
    
    window.dataLayer.push({
      "cartItems": cartItemSkus,
      "shopifyCartItemSkus": shopifyCartItemSkus,
    });
  }

  const total = Number(theme.cartData.total_price / 100).toFixed(2);
  window.dataLayer.push({
    "cartTotal": total,
    "ecommerce": {
      "currencyCode": theme.cartData.currency,
      "actionField": {list: 'Shopping Cart'},
      "impressions": cartItems,
    },
  });
}

function gtmTrackCartAddToCart(item, atc_type) {
  const items = [item];
  const addedItems = items.map((item, idx) => {
    return {
      position: idx,
      id: item.sku,
      productId: item.product_id,
      variantId: item.variant_id,
      image: item.image,
      shopifyId: 'shopify_US_' + item.product_id + '_' + item.variant_id,
      name: item.product_title.replace("'", ''),
      quantity: item.quantity,
      price: item.price / 100,
      brand: item.vendor.replace("'", ''),
      variant: item.variant_title,
      location: 'Cart',
      addType: atc_type,
    };
  });

  window.dataLayer.push({
    "event": "addToCart",
    "ecommerce": {
      "currencyCode": ShopifyAnalytics.meta.currency,
      "add": {
        "products": addedItems,
      },
    },
  });
}

function gtmTrackBundleAddToCart(items) {
  const addedItems = items.map((item, idx) => {
    return {
      position: idx,
      id: item.sku,
      productId: item.product_id,
      variantId: item.variant_id,
      image: item.image,
      shopifyId: 'shopify_US_' + item.product_id + '_' + item.variant_id,
      name: item.product_title.replace("'", ''),
      quantity: item.quantity,
      price: item.price / 100,
      brand: item.vendor.replace("'", ''),
      variant: item.variant_title,
      bundle: item.properties && item.properties._bundle,
      location: 'PDP',
      addType: 'Bundle',
    };
  });

  window.dataLayer.push({
    event: 'addToCart',
    ecommerce: {
      currencyCode: ShopifyAnalytics.meta.currency,
      add: {
        products: addedItems,
      },
    },
  });  
}

function gtmTrackRemoveItem(key) {
  const keys = [].concat(key);

  const cartItems = theme.cartData.items.map((item, idx) => {
    return {
      position: idx,
      id: item.sku,
      key: item.key,
      productId: item.product_id,
      variantId: item.variant_id,
      shopifyId: 'shopify_US_' + item.product_id + '_' + item.variant_id,
      name: item.product_title.replace("'", ''),
      quantity: item.quantity,
      price: item.price / 100,
      brand: item.vendor.replace("'", ''),
      variant: item.variant_title,
    };
  });

  const items = cartItems.filter((item) => {
    return keys.includes(item.key);
  })

  if (!items) return;

  items.forEach((item) => {
    delete item.key;
  });

  window.dataLayer.push({
    event: 'removeFromCart',
    ecommerce: {
      remove: {
        products: items,
      },
    },
  });
}


export function autoAddProduct(cartState) {
  if (window.theme.autoAddProduct) {
    // console.log('auto add product?', cartState);
    const variantId = window.theme.autoAddProduct.variantId;
    const priceThreshold = window.theme.autoAddProduct.priceThreshold;
    let promoTitle = 'Free Gift';
    if (window.theme.autoAddProduct.promoTitle !== '') {
      promoTitle = window.theme.autoAddProduct.promoTitle;
    }
    const existingPromo = cartState.items.filter(
      (item) => item.properties._promo
    );
    const notGiftCardAndSample = cartState.items.filter((item) => {
      if (item.gift_card == false && item.product_type != 'sample') {
        return item;
      }
    });

    // Check if only promo & gift card
    const onlyPromoOrGiftCardOrSample = cartState.items.filter((item) => {
      if (
        item.properties._promo ||
        item.gift_card == true ||
        item.product_type == 'sample'
      ) {
        return item;
      }
    });

    if (
      cartState.item_count > 0 &&
      existingPromo.length === 0 &&
      notGiftCardAndSample.length > 0 &&
      cartState.total_price >= priceThreshold &&
      variantId !== 0
    ) {
      addItem(
        variantId,
        {
          quantity: 1,
          properties: {
            _promo: promoTitle,
            _bundle_title: promoTitle,
            _d: 0,
          },
        },
        'cart',
        'auto-add'
      );
    }

    // Check to see if promo needs to be removed
    if (existingPromo.length > 0) {
      const promoKey = existingPromo[0].key;
      // Only gift cards and promo
      if (onlyPromoOrGiftCardOrSample.length == cartState.items.length) {
        cartApi.removeItem(promoKey).then((state) => {
          gtmTrackRemoveItem(promoKey);
          updateCart(state);
          refreshCart();
        });
      }

      // Below price threshold
      if (cartState.total_price < priceThreshold) {
        cartApi.removeItem(promoKey).then((state) => {
          gtmTrackRemoveItem(promoKey);
          updateCart(state);
          refreshCart();
        });
      }
    }
  }
}

export function updateSampleCards(cartState) {
  let sampleIds = [];
  let sampleSkus = [];
  let containsFullProduct = false;
  let giftWrapItem = false;

  let fullSizeIds = [];

  cartState.items.forEach(function (item) {
    if (item.product_type == 'sample') {
      sampleIds.push(item.product_id);
      sampleIds.push(item.variant_id);
      // If item.properties._bundle exists, add it to the sampleIds array
      if (item.properties._bundle) {
        sampleSkus.push(item.properties._bundle);
      }
      sampleSkus.push(item.sku);
    } else {
      containsFullProduct = true;
    }

    fullSizeIds.push(item.product_id);

    if (item.title.toLowerCase().includes('gift wrapping')) {
      giftWrapItem = true;
    }
  });

  if (containsFullProduct && !giftWrapItem) {
    cartGiftWrap.classList.remove('hidden');
  } else {
    cartGiftWrap.classList.add('hidden');
  }

  // Loop through each sample
  const samples = document.querySelectorAll(selectors.sampleItem);
  samples.forEach(function (sample) {
    const sampleItem = Number(sample.dataset.sampleItem);
    const sampleSku = sample.dataset.sampleItemSku;
    sample.classList.remove('sample--disabled');

    if (sampleIds.includes(sampleItem) || sampleSkus.includes(sampleSku)) {
      sample.classList.add('sample--disabled');
    }

    const sampleFullSizeId = Number(sample.dataset.fullSize);
    if (fullSizeIds.includes(sampleFullSizeId)) {
      sample.classList.add('sample--disabled');
    }
  });
}

export function updateUpsellCards(cartState) {
  const cartUpsellItems = cartDrawer.querySelectorAll(
    '[data-cart-upsell-item]'
  );

  const upsellCount = cartUpsellItems.length;
  let upsellHiddenCount = 0;

  let upsellItemProductIds = [];

  cartUpsellItems.forEach(function (upsellItem) {
    const addToCartButton = upsellItem.querySelector('[data-variant-id]');
    const upsellItemId = Number(addToCartButton.dataset.variantId);
    const upsellProductId = Number(upsellItem.dataset.cartUpsellItem);
    upsellItems.push(upsellItemId);
    upsellItemProductIds.push(upsellProductId);
  });

  cartState.items.forEach(function (item) {
    const productId = Number(item.product_id);
    // update this array with new data
    if (upsellItemProductIds.includes(productId)) {
      const matchItem = cartUpsell.querySelector(
        `[data-cart-upsell-item="${productId}"]`
      );
      matchItem.classList.add('hidden');
    }
  });

  // hide item types based on last add
  // if (cartState.items.length > 0) {
  //   const lastProduct = cartState.items[0];
  //   const lastProductType = lastProduct.product_type.replace(/\s+/g, '-').toLowerCase();
  //   if (
  //     lastProductType == 'set' ||
  //     lastProductType == 'bundle' ||
  //     lastProduct.properties['_bundle']
  //   ) {
  //     // Bundle or set last item added
  //     const bundleUpsellItems = cartUpsell.querySelectorAll(
  //       '[data-cart-upsell-item-type="bundle"], [data-cart-upsell-item-type="set"]'
  //     );
  //     bundleUpsellItems.forEach(function(item){
  //       item.classList.add('hidden');
  //     });
  //   } else {
  //     const notBundleUpsellItems = cartUpsell.querySelectorAll(
  //       '.cart-drawer__upsell-item:not([data-cart-upsell-item-type="bundle"]):not([data-cart-upsell-item-type="set"])'
  //     );
  //     notBundleUpsellItems.forEach(function (item) {
  //       item.classList.add('hidden');
  //     });
  //   }
  // }

  // Check for duplicate upsell items
  const uniqueUpsellProductIds = [...new Set(upsellItemProductIds)];
  let duplicates = [...upsellItemProductIds];
  uniqueUpsellProductIds.forEach((item) => {
    const i = duplicates.indexOf(item);
    duplicates = duplicates
      .slice(0, i)
      .concat(duplicates.slice(i + 1, duplicates.length));
  });

  duplicates.forEach((id) => {
    const duplicateUpsellItems = cartDrawer.querySelectorAll(
      `[data-cart-upsell-item="${id}"]`
    );
    duplicateUpsellItems.forEach((item, index) => {
      if (index === 0) return;
      item.classList.add('hidden');
    });
  });

  // check if upsell items is empty
  cartUpsellItems.forEach((item) => {
    if (item.classList.contains('hidden')) {
      upsellHiddenCount++;
    }
  });
  if (upsellCount == upsellHiddenCount || cartState.item_count == 0) {
    cartUpsell.classList.add('hidden');
  } else {
    cartUpsell.classList.remove('hidden');
  }
}

